import {
  DOCS_FEEDBACK_RATING_TYPES,
  QUERY_PROGRESS_SUCCEED,
} from "core/consts";
import { activateDocsFeedback } from "core/model/utils/featureFlags";
import { DocsFeedbackRatingTypes, DocsFieldFeedback } from "core/types";
import { Button, ControlledTextarea, Tooltip } from "ds/ui";
import { useCopy } from "dsl/atoms/CopyButton/useCopy";
import {
  CheckIcon,
  CopyIcon,
  ThumbsDownIcon,
  ThumbsUpIcon,
} from "lucide-react";
import { ComponentProps } from "react";
import { useWatch } from "react-hook-form";
import { useTranslations } from "translations";

type DischargeLetterTextareaActionsProps = {
  label: string;
  fieldName: string;
  fieldRating?: DocsFeedbackRatingTypes;
  onFieldFeedback?: (feedback: DocsFieldFeedback | null) => void;
};

function DischargeLetterTextareaActions({
  fieldName,
  fieldRating,
  label,
  onFieldFeedback,
}: DischargeLetterTextareaActionsProps) {
  const translations = useTranslations();
  const fieldValue = useWatch({ name: fieldName });
  const { copyMessage, copyStatus, copyToClipboard } = useCopy({
    onCopyMessage: translations.chat.copyMessage,
  });

  const isPositive = fieldRating === DOCS_FEEDBACK_RATING_TYPES.POSITIVE;
  const isNegative = fieldRating === DOCS_FEEDBACK_RATING_TYPES.NEGATIVE;

  const copyButton = (
    <Button
      aria-label={translations.chat.copyMessage}
      color="black"
      id={`copy-button-${fieldName}`}
      isDisabled={!fieldValue}
      isIconOnly
      size="sm"
      trackEventProps={{ fieldName, id: "copy-button" }}
      variant="light"
      onPress={() => copyToClipboard(fieldValue)}
    >
      {copyStatus === QUERY_PROGRESS_SUCCEED ? (
        <CheckIcon size={14} />
      ) : (
        <CopyIcon size={14} />
      )}
    </Button>
  );

  return (
    <div className="absolute right-0 bottom-0 flex items-center gap-1">
      <Button
        aria-label={translations.actions.accept}
        color={isPositive ? "success" : "black"}
        id={`positive-feedback-${fieldName}`}
        isIconOnly
        size="sm"
        variant="light"
        onPress={() =>
          onFieldFeedback?.({
            fieldName,
            label,
            rating: DOCS_FEEDBACK_RATING_TYPES.POSITIVE,
          })
        }
      >
        <ThumbsUpIcon size={14} />
      </Button>
      <Button
        aria-label={translations.actions.reject}
        color={isNegative ? "danger" : "black"}
        id={`negative-feedback-${fieldName}`}
        isIconOnly
        size="sm"
        variant="light"
        onPress={() =>
          onFieldFeedback?.({
            fieldName,
            label,
            rating: DOCS_FEEDBACK_RATING_TYPES.NEGATIVE,
          })
        }
      >
        <ThumbsDownIcon size={14} />
      </Button>
      {copyStatus === QUERY_PROGRESS_SUCCEED ? (
        <Tooltip content={copyMessage} isOpen placement="bottom">
          {copyButton}
        </Tooltip>
      ) : (
        copyButton
      )}
    </div>
  );
}

export type DischargeLetterTextareaProps = ComponentProps<
  typeof ControlledTextarea
> &
  Pick<
    DischargeLetterTextareaActionsProps,
    "fieldRating" | "onFieldFeedback" | "label"
  >;

export function DischargeLetterTextarea({
  fieldRating,
  label,
  name,
  onFieldFeedback,
  ...props
}: DischargeLetterTextareaProps) {
  return (
    <ControlledTextarea
      label={label}
      name={name}
      {...props}
      classNames={
        activateDocsFeedback
          ? { innerWrapper: "relative", input: "pb-8" }
          : undefined
      }
      endContent={
        activateDocsFeedback && (
          <DischargeLetterTextareaActions
            fieldName={name}
            fieldRating={fieldRating}
            label={label}
            onFieldFeedback={onFieldFeedback}
          />
        )
      }
    />
  );
}
